import { customEventFactory } from '@dreryk/react-common';
import { TextEditorValue } from '@dreryk/react-text-editor';

import { PreInterviewItem } from '../../common/preInterview/PreInterviewPreviewItem.types';

import { ProcedureTableBean } from './tabs/procedures/procedures.types';

const VISIT_FORM_SYNCHRONIZATION_EVENT = 'VisitFormSynchronizationEvent';

export type DiagnosisChangeValue = {
   code: string;
   name: string;
   additionalInformation?: string | null;
   main?: boolean;
};

export type DiagnosisChangeBehavior = 'prepend' | 'append';

export type DiagnosesChangeData = {
   value: Array<DiagnosisChangeValue>;
   behavior: DiagnosisChangeBehavior;
};

export type ProcedureChangeValue = ProcedureTableBean;

export type ProceduresChangeBehavior = 'replace';

export type ProceduresChangeData = {
   value: Array<ProcedureChangeValue>;
   behavior: ProceduresChangeBehavior;
};

export type VisitFormSynchronizationEventInternalData = {
   interview?: TextEditorValue | PreInterviewItem[] | string;
   subjectiveAssessment?: TextEditorValue | string;
   recommendations?: TextEditorValue | string;
   diagnoses?: DiagnosesChangeData;
   procedures?: ProceduresChangeData;
};

export type VisitFormSynchronizationEventData = {
   interview?: TextEditorValue;
   subjectiveAssessment?: TextEditorValue;
   recommendations?: TextEditorValue;
   diagnoses?: Array<DiagnosisChangeValue>;
   procedures?: Array<ProcedureChangeValue>;
};

export type VisitDetailType = keyof VisitFormSynchronizationEventInternalData;

const [
   dispatchEvent,
   registerVisitFormSynchronizationEvent,
] = customEventFactory<VisitFormSynchronizationEventInternalData>(VISIT_FORM_SYNCHRONIZATION_EVENT);

export const dispatchVisitDetailCopyEvent = <TYPE extends VisitDetailType>(
   type: TYPE,
   value: VisitFormSynchronizationEventInternalData[TYPE]
): boolean => {
   return dispatchEvent({ [type]: value });
};

export const dispatchVisitDetailsCopyEvent = (data: VisitFormSynchronizationEventInternalData): boolean =>
   dispatchEvent({
      interview: data.interview,
      subjectiveAssessment: data.subjectiveAssessment,
      recommendations: data.recommendations,
      diagnoses: data.diagnoses,
   });

export const dispatchVisitDiagnosisPrependEvent = (diagnosis: DiagnosisChangeValue): boolean =>
   dispatchEvent({
      diagnoses: {
         behavior: 'prepend',
         value: [diagnosis],
      },
   });

export const dispatchVisitDiagnosesAppendEvent = (diagnoses: DiagnosisChangeValue[]): boolean =>
   dispatchEvent({
      diagnoses: {
         behavior: 'append',
         value: diagnoses,
      },
   });

export const dispatchVisitProceduresReplaceEvent = (procedures: ProcedureChangeValue[]): boolean =>
   dispatchEvent({
      procedures: {
         behavior: 'replace',
         value: procedures,
      },
   });

export { registerVisitFormSynchronizationEvent };
